import type { AccountingExpUpdate, GameStateSnapshot, RankExpPoint } from '@/domain/types'
import type { GamePreset } from '@/presets/types'

const EXACT_EXP_RATIO_TOLERANCE = 0.0002

export function exactSessionExpPoint(preset: GamePreset, state: GameStateSnapshot | null): RankExpPoint | null {
  if (!state || state.level === null || state.exp.absolute === null || state.exp.estimated) return null
  const required = preset.experienceToNext(state.level)
  if (required === null || required <= 0 || state.exp.absolute < 0 || state.exp.absolute > required) return null
  if (state.exp.ratio !== null && Math.abs(state.exp.absolute / required - state.exp.ratio) > EXACT_EXP_RATIO_TOLERANCE) return null
  return { level: state.level, absolute: state.exp.absolute }
}

export class SessionExpCounter {
  private completedGain = 0
  private segmentStart: RankExpPoint | null = null
  private segmentCurrent: RankExpPoint | null = null
  private segmentGain = 0
  private hasBaseline = false

  constructor(private readonly preset: GamePreset) {}

  reset(initialState: GameStateSnapshot | null = null): void {
    this.completedGain = 0
    this.segmentStart = null
    this.segmentCurrent = null
    this.segmentGain = 0
    this.hasBaseline = false
    const point = exactSessionExpPoint(this.preset, initialState)
    if (point) this.establish(point)
  }

  breakContinuity(): void {
    this.completeSegment()
  }

  ingest(update: AccountingExpUpdate): void {
    if (update.continuity === 'rebaseline') {
      this.completeSegment()
      if (update.exact && update.end) this.establish(update.end)
      return
    }
    if (!update.exact || !update.end) return
    if (update.continuity === 'ignored'
      || update.continuity === 'pending'
      || update.continuity === 'unavailable') return

    if (!this.segmentStart || !this.segmentCurrent) {
      this.establish(update.end)
      return
    }
    if (!this.canAdvance(this.segmentCurrent, update.end, update)) {
      this.completeSegment()
      this.establish(update.end)
      return
    }

    const gain = this.gainBetween(this.segmentStart, update.end)
    if (gain === null) {
      this.completeSegment()
      this.establish(update.end)
      return
    }
    this.segmentCurrent = update.end
    this.segmentGain = gain
  }

  total(): number | null {
    return this.hasBaseline ? this.completedGain + this.segmentGain : null
  }

  private establish(point: RankExpPoint): void {
    this.segmentStart = point
    this.segmentCurrent = point
    this.segmentGain = 0
    this.hasBaseline = true
  }

  private completeSegment(): void {
    this.completedGain += this.segmentGain
    this.segmentStart = null
    this.segmentCurrent = null
    this.segmentGain = 0
  }

  private canAdvance(previous: RankExpPoint, current: RankExpPoint, update: AccountingExpUpdate): boolean {
    if (current.level === previous.level) return current.absolute >= previous.absolute
    if (current.level !== previous.level + 1 || update.delta === null || !update.start) return false
    return update.start.level === previous.level
      && update.start.absolute === previous.absolute
      && update.delta >= 0
  }

  private gainBetween(start: RankExpPoint, end: RankExpPoint): number | null {
    if (end.level < start.level) return null
    if (end.level === start.level) {
      const delta = end.absolute - start.absolute
      return delta >= 0 ? delta : null
    }

    const startRequired = this.preset.experienceToNext(start.level)
    if (startRequired === null || startRequired <= 0 || start.absolute > startRequired) return null
    let gain = startRequired - start.absolute
    for (let level = start.level + 1; level < end.level; level += 1) {
      const required = this.preset.experienceToNext(level)
      if (required === null || required <= 0) return null
      gain += required
    }
    const endRequired = this.preset.experienceToNext(end.level)
    if (endRequired === null || endRequired <= 0 || end.absolute > endRequired) return null
    return gain + end.absolute
  }
}
